import {EVENT_DETAIL} from "@COMPONENTS/common/constants";
import publicRuntimeConfig from "@FRAMEWORK/config/public";
import useConfigureAxios from "@FRAMEWORK/utils/useConfigureAxios";
import {Response} from "@QUERIES/types";
import {UseQueryOptions, useQuery} from "@tanstack/react-query";

interface keypointCont {
	kpointCont: string;
	sortOrder: number;
}
interface EventDetail {
	inArrvTm: string;
	inFlightNm: string;
	webDisplayCls: string;
	trCompanySnm: string /* 출발 항공 코드 */;
	inAirCd: string /* 도착 항공 코드 */;
	inAirNm: string /* 도착 항공 명 */;
	fakeCnt: number;
	rervCnt: number;
	trTp: string;
	dayCnt: number;
	tourPatInfo: string;
	evStartDt: string;
	trCompanyNm: string;
	outDayDiff: number;
	avlilBlockQty: number;
	inDeprtTm: string;
	evStaCd: string;
	evArriveDt: string;
	outFlightNm: string;
	outDeprtTm: string;
	minCnt: number;
	goodsTp: string;
	inDayDiff: string;
	outArrvTm: string;
	dayStayInfo: string;
	accomNm: string;
	goodsAreaOne: string;
	goodTravelYn: string;
	evCd: string;
	evNm: string;
	goodsBrandCd: string;
	emphasisDivi: string;
	rvwAvgScore: number;
	rvwCnt: number;
	epiCnt: number;
	startCharDivi: string;
	webStartConfYn: string;
	webCancelCommiYn: string /* 취소수수료 여부 N: 특별약관 | Y: 일반약관 */;
	webTourDayYn: string;
	webGoodsPriceYn: string;
	webAirConfYn: string;
	webAccommConfYn: string;
	webGuidConfYn: string;
	webInsolConfYn: string;
	shopCnt: number;
	goodsCd: string;
	cancelCnt: number;
	freeScheYn: string /* 자유 일정 여부 */;
	expCurrCd: string /* 가이드경비 화폐코드 */;
	guideExpAmt: number /* 가이드경비 */;
	noTosYn: string /** 노팁/노옵션/노쇼핑 여부 */;
	stopoverCntYn: string /* 경유 횟수 노출 여부 */;
	inStovCnt: number /* 입국 경유 횟수 */;
	outStovCnt: number /* 출국 경유 횟수 */;
	singleChagYn: string /* 싱글차지유무 */;
	singleChagAmt: number /* 싱글차지금액 */;
	mgmUserInfo: string /* 대표번호노출 여부 Y/N */;
	mgmDeptNm: string /* 부서명 */;
	mgmUserNm: string /* 담당자명 */;
	mgmUserTelNo: string /* 담당자 대표번호 */;
	mgmUserEmail: string /* 담당자 이메일 */;
	phoneDirect: string /* 대표번호 */;
	keypointContList: keypointCont[] /* 여행포인트 */;
	evProgDivi: string;
	evProgDiviNm: string;
	insuTargetAttr: string /* 보험정보 */;
	insuTargetExpl: string /* 보험정보 */;
	simpleScheYn: string /* 간략일정 노출 여부 Y | N */;
	eventTypeCd: string /* 공통코드 EVENT_TYPE_CD */;
	outUseTmhh: string /* 출국 시간 */;
	outUseTmmi: string /* 출국 분 */;
	inUseTmhh: string /* 입국 시간 */;
	inUseTmmi: string /* 입국 분 */;
	seatClassCd: string /* 죄석등급 */;
	seatClassNm: string /* 죄석등급 이름 */;
	greenFeeYn: string /* 그린피 포함여부(골프) */;
	caddieFeeYn: string /* 캐디피 포함여부(골프) */;
	holeCnt: number /* 홀수(골프) */;
	continentCd: string; // Braze
	continentNm: string; // Braze
	countryCd: string; // Braze
	countryNm: string; // Braze
	fixRervCd: "YT" | "ND" | null; // 예약코드 (null - 가예약 | ND - 가예약(예약금) | YT - 실예약(전액))
	familyYn: "Y" | "N"; // 패밀리 상품 여부
}

const key = `useEventDetail`;

/** ```
 * 행사 정보 조회
 *   `/pkg/api/event/{evCd}`
 * parameter
 *   `evCd` 행사코드
 * ```*/
const getPath = (evCd: string): string | null => {
	if (!evCd) return null;
	return `${publicRuntimeConfig.ENV_PORTAL_API_URL}/pkg/event/${evCd}`;
};

/** ```
 * 행사 정보 조회
 *   `/pkg/api/event/{evCd}`
 * parameter
 *   `evCd` 행사코드
 * ```*/
const eventDetailAsync = (evCd: string) =>
	useConfigureAxios(getPath(evCd), {
		method: "GET",
	});

/** ```
 * 행사 정보 조회
 *   `/pkg/api/event/{evCd}`
 * parameter
 *   `evCd` 행사코드
 * ```*/
const eventDetailSSR = (evCd: string) =>
	useConfigureAxios(getPath(evCd), {
		method: "GET",
	});

/** ```
 * 행사 정보 조회
 *   `/pkg/api/event/{evCd}`
 * parameter
 *   `evCd` 행사코드
 * ```*/
const useEventDetail = (evCd: string, options?: Partial<UseQueryOptions<Response<EventDetail>, Error>>) =>
	useQuery({
		queryKey: [key, getPath(evCd)],
		queryFn: () => useConfigureAxios({url: getPath(evCd)}).then(r => r.data),
		staleTime: 1000 * 30 * 60,
		placeholderData: {body: EVENT_DETAIL},
		...options,
	});

export default useEventDetail;
export {eventDetailAsync, eventDetailSSR, getPath, key};
export type {EventDetail};
